import { Stack, Typography } from '@mui/material';
import { ReadonlyVec3 } from 'gl-matrix';
import { useCallback } from 'react';
import NumberInput from '~/components/Inputs/NumberInput';
import { roundMmToHundredthCm } from '~/utils/units';

const MM_PER_CM = 10;

const AXES = [
    { label: 'X', index: 0 },
    { label: 'Y', index: 1 },
    { label: 'Z', index: 2 },
] as const;

interface PointCoordinateEditorProps {
    coordinatesMm: ReadonlyVec3;
    onChange: (coords: { x: number; y: number; z: number }) => void;
    disabled: boolean;
}

/** Editable X / Y / Z fields for point ROIs. Displays in cm, commits in mm. */
export default function PointCoordinateEditor({
    coordinatesMm,
    onChange,
    disabled,
}: PointCoordinateEditorProps) {
    const handleAxisChange = useCallback(
        (axisIndex: number, valueCm: number | null) => {
            if (valueCm === null) {
                return;
            }
            const coords = {
                x: coordinatesMm[0],
                y: coordinatesMm[1],
                z: coordinatesMm[2],
            };
            if (axisIndex === 0) {
                coords.x = valueCm * MM_PER_CM;
            } else if (axisIndex === 1) {
                coords.y = valueCm * MM_PER_CM;
            } else {
                coords.z = valueCm * MM_PER_CM;
            }
            onChange(coords);
        },
        [coordinatesMm, onChange]
    );

    return (
        <Stack spacing={1.5}>
            <Typography fontSize={12} fontWeight={500} color="text.secondary">
                Marker Position (cm)
            </Typography>
            <Stack direction="row" spacing={1}>
                {AXES.map(({ label, index }) => (
                    <NumberInput
                        key={label}
                        value={roundMmToHundredthCm(coordinatesMm[index])}
                        onChange={(v) => handleAxisChange(index, v)}
                        step={0.01}
                        precision={2}
                        disabled={disabled}
                        label={label}
                        fullWidth
                    />
                ))}
            </Stack>
        </Stack>
    );
}
